// Route tree roots. Module lanes import `appRoute` to hang their pages under the signed-in layout:
//
//   import { createRoute, lazyRouteComponent } from '@tanstack/react-router'
//   import { appRoute } from '../../app/route-base'
//   export const meRoute = createRoute({
//     getParentRoute: () => appRoute,
//     path: '/me',
//     component: lazyRouteComponent(() => import('./MyCompliancePage'), 'MyCompliancePage'),
//   })
//
// This file must not import src/modules (it is imported by them). The layout component is attached in router.tsx.
import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router'
import { isApiError } from '../lib/api'
import type { Me } from '../lib/auth'
import { meQueryOptions } from '../lib/auth-context'
import { homePath, isEmployeeOnly } from '../lib/permissions'

export interface RouterContext {
  queryClient: QueryClient
}

export const rootRoute = createRootRouteWithContext<RouterContext>()()

function employeeAllowed(pathname: string): boolean {
  return pathname === '/me' || pathname.startsWith('/me/')
}

/**
 * The signed-in layout. Its guard loads the session (GET /auth/me) before any page renders:
 * anonymous visitors go to /login with a returnTo, employee-only users are kept inside My Compliance, and a narrow
 * role opening the app root lands on its own first page (an Access Reviewer on Access Reviews, not the dashboard).
 */
export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }): Promise<{ me: Me }> => {
    let me: Me
    try {
      me = await context.queryClient.ensureQueryData(meQueryOptions)
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        throw redirect({ to: '/login', search: { returnTo: location.href } })
      }
      throw err
    }
    const viewer = { roles: me.roles, isGuest: me.isGuest }
    if (isEmployeeOnly(viewer) && !employeeAllowed(location.pathname)) {
      throw redirect({ to: '/me' })
    }
    const home = homePath(viewer)
    if (location.pathname === '/' && home !== '/') throw redirect({ to: home })
    return { me }
  },
})
